/**
 * NarcoticsReportPDF — Controlled Substance & Narcotic Monthly Count
 * 3-page @react-pdf/renderer report:
 *   Page 1: Cover page (client info, substances, staff, legend)
 *   Page 2: Count matrix (days x shifts per medication)
 *   Page 3: Discrepancy log
 */
import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import moment from "moment";
import { getActiveSlotForDate } from "../../utils";

/* ─── Colour palette ─── */
const C = {
  dark: "#1e2533",
  darkAlt: "#2D3748",
  white: "#FFFFFF",
  offWhite: "#F5F3EE",
  amber: "#E6A817",
  amberLight: "#FFF8E1",
  amberBorder: "#FFE082",
  red: "#C62828",
  redLight: "#FFEBEE",
  green: "#2E7D32",
  greenLight: "#E8F5E9",
  grey: "#757575",
  greyLight: "#F5F5F5",
  greyBorder: "#E0E0E0",
  greyDark: "#424242",
  text: "#1C1917",
  textMuted: "#78716C",
  border: "#E4E0D8",
};

/* ─── Helper: get initials from name ─── */
const getInitials = (name) => {
  if (!name) return "?";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

/* ─── Helper: map timeSlot string to shift category ─── */
const getShiftFromTimeSlot = (timeSlot) => {
  if (!timeSlot) return "NOC";
  // Parse hour from "01:00 AM", "02:00 PM" etc.
  const match = timeSlot.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return "NOC";
  let hour = parseInt(match[1], 10);
  const period = match[3].toUpperCase();
  if (period === "AM" && hour === 12) hour = 0;
  if (period === "PM" && hour !== 12) hour += 12;
  // AM shift: 6:00 - 13:59, PM shift: 14:00 - 21:59, NOC: 22:00 - 5:59
  if (hour >= 6 && hour < 14) return "AM";
  if (hour >= 14 && hour < 22) return "PM";
  return "NOC";
};

/* ─── Helper: get staff initials from staff object ─── */
const getStaffInitials = (staff) => {
  if (!staff) return "";
  const name = staff?.name || staff?.firstName || "";
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/* ─── Process MAR data into matrix structure ─── */
const processNarcoticData = (marRecords, narcoticMeds, month, year) => {
  const daysInMonth = moment(`${year}-${String(month).padStart(2, "0")}`, "YYYY-MM").daysInMonth();
  const shifts = ["AM", "PM", "NOC"];

  // Build medication matrix
  const medMatrix = {};
  const staffSet = {};
  const discrepancies = [];

  narcoticMeds.forEach((med) => {
    const medId = String(med.id || med.medicationId || med._id);
    if (!medId || medId === "undefined") return;
    // Determine discontinuation day (if discontinued during this month)
    let dcDay = null;
    if (med.discontinued) {
      const dcDate = med.discontinuedDate || med.discontinuedAt || med.discontinuedOn || med.updatedAt;
      if (dcDate) {
        const dcMoment = moment(dcDate);
        if (dcMoment.month() + 1 === month && dcMoment.year() === year) {
          dcDay = dcMoment.date();
        } else if (dcMoment.isBefore(moment(`${year}-${String(month).padStart(2, "0")}-01`))) {
          dcDay = 0; // discontinued before this month — all cells are D/C
        }
      }
    }
    medMatrix[medId] = {
      medication: med,
      openingCount: null, // computed below after filling the matrix
      dcDay,
      days: {},
    };
    for (let d = 1; d <= daysInMonth; d++) {
      medMatrix[medId].days[d] = { AM: null, PM: null, NOC: null };
    }
  });

  // Sort MAR records by date ascending so first-record detection is correct
  const sortedRecords = [...(marRecords || [])].sort(
    (a, b) => new Date(a.date || a.createdAt) - new Date(b.date || b.createdAt)
  );

  // Fill matrix from MAR records.
  // Calendar view entries (/mar/getCalendarView) have timeSlot as a plain string
  // (one entry per slot per day). Legacy /mar/get entries may carry an array —
  // getActiveSlotForDate handles both cases.
  sortedRecords.forEach((rec) => {
    const medId = String(rec.medicationId);
    if (!medMatrix[medId]) return;

    const recDate = moment(rec.date || rec.createdAt);
    if (recDate.month() + 1 !== month || recDate.year() !== year) return;

    const day = recDate.date();
    const slotStr = typeof rec.timeSlot === "string"
      ? rec.timeSlot
      : getActiveSlotForDate(rec.timeSlot, rec.date);
    const shift = getShiftFromTimeSlot(slotStr);
    const remaining = rec.medicineRemaining === "unlimited" ? null : parseInt(rec.medicineRemaining, 10);
    const staffInitial = getStaffInitials(rec.staff);

    if (staffInitial && rec.staff) {
      const sKey = staffInitial;
      if (!staffSet[sKey]) {
        staffSet[sKey] = {
          initials: staffInitial,
          name: rec.staff?.name || rec.staff?.firstName || "Unknown",
        };
      }
    }

    // Keep the latest administered record for each day/shift/med
    const recTs = rec.administeredAt || rec.createdAt || rec.date;
    const existing = medMatrix[medId].days[day]?.[shift];
    if (!existing || new Date(recTs) > new Date(existing.createdAt)) {
      medMatrix[medId].days[day][shift] = {
        count: isNaN(remaining) ? null : remaining,
        staffInitial,
        createdAt: recTs,
      };
    }
  });

  // Compute opening and closing counts from the filled matrix.
  // Opening count = first recorded remaining count + dose per administration
  // (approximates the count just before the first dose of the month).
  Object.keys(medMatrix).forEach((medId) => {
    const entry = medMatrix[medId];
    const med = entry.medication;
    const dosePerAdmin = Number(med?.medicationadministered || med?.medicationAdministered) || 1;
    let firstCount = null;
    let lastKnownCount = null;

    for (let d = 1; d <= daysInMonth; d++) {
      for (const shift of shifts) {
        const cell = entry.days[d]?.[shift];
        if (cell && cell.count !== null && cell.count !== undefined) {
          if (firstCount === null) firstCount = cell.count;
          lastKnownCount = cell.count;
        }
      }
    }

    if (firstCount !== null) {
      entry.openingCount = firstCount + dosePerAdmin;
    } else {
      // No records this month — fall back to current medicineRemaining
      const remainMed = parseInt(med?.medicineRemaining, 10);
      entry.openingCount = !isNaN(remainMed) && med?.medicineRemaining !== "unlimited"
        ? remainMed
        : 0;
    }
    entry.closingCount = lastKnownCount ?? entry.openingCount;
  });

  // Build staff array
  const staffList = Object.values(staffSet);

  return { medMatrix, daysInMonth, staffList, discrepancies };
};

/* ─── Styles ─── */
const s = StyleSheet.create({
  page: { fontFamily: "Helvetica", fontSize: 8, color: C.text, backgroundColor: C.offWhite },
  pageLandscape: { fontFamily: "Helvetica", fontSize: 6, color: C.text, backgroundColor: C.white, padding: "20px 16px" },

  /* Cover header */
  coverHeader: { backgroundColor: C.dark, padding: "28px 36px 24px" },
  coverTopRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 },
  coverLogoRow: { flexDirection: "row", alignItems: "center", gap: 10 },
  coverLogoCircle: { width: 36, height: 36, borderRadius: 18, backgroundColor: C.amber, justifyContent: "center", alignItems: "center" },
  coverLogoText: { fontSize: 13, fontWeight: "bold", color: C.white },
  coverLogoImg: { width: 36, height: 36, borderRadius: 18, objectFit: "cover" },
  coverGenText: { fontSize: 7, color: "#9CA3AF", textAlign: "right" },

  coverBadge: { backgroundColor: C.red, borderRadius: 4, paddingVertical: 4, paddingHorizontal: 14, alignSelf: "flex-start", marginBottom: 14 },
  coverBadgeText: { fontSize: 8, fontWeight: "bold", color: C.white, letterSpacing: 0.3 },

  coverClientName: { fontSize: 26, fontWeight: "bold", color: C.white, marginBottom: 4 },
  coverSubtitle: { fontSize: 9, color: "#9CA3AF", marginBottom: 0 },

  /* Cover body */
  coverBody: { padding: "24px 36px" },
  sectionTitle: { fontSize: 8, fontWeight: "bold", color: C.greyDark, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 10, marginTop: 18 },
  infoGrid: { flexDirection: "row", gap: 40 },
  infoCol: { flex: 1 },
  infoRow: { flexDirection: "row", marginBottom: 7, borderBottomWidth: 0.5, borderBottomColor: C.border, paddingBottom: 5 },
  infoLabel: { fontSize: 8, color: C.textMuted, width: 90 },
  infoValue: { fontSize: 8, fontWeight: "bold", color: C.text, flex: 1 },
  infoValueHighlight: { fontSize: 8, fontWeight: "bold", color: C.amber },
  infoValueRed: { fontSize: 8, fontWeight: "bold", color: C.red },

  /* Substance cards */
  substanceRow: { flexDirection: "row", gap: 10, marginBottom: 16 },
  substanceCard: { flex: 1, borderWidth: 1, borderColor: C.greyBorder, borderRadius: 4, padding: "10px 12px", backgroundColor: C.white },
  substanceName: { fontSize: 9, fontWeight: "bold", color: C.text, marginBottom: 2 },
  substanceDetail: { fontSize: 7, color: C.textMuted, marginBottom: 6 },
  substanceCount: { fontSize: 7, fontWeight: "bold", color: C.amber },

  /* Staff cards */
  staffRow: { flexDirection: "row", gap: 10, flexWrap: "wrap", marginBottom: 16 },
  staffCard: { flexDirection: "row", alignItems: "center", gap: 8, borderWidth: 1, borderColor: C.greyBorder, borderRadius: 4, padding: "8px 14px", backgroundColor: C.white, minWidth: 120 },
  staffCircle: { width: 28, height: 28, borderRadius: 14, backgroundColor: "#FFF0F0", justifyContent: "center", alignItems: "center" },
  staffInitial: { fontSize: 9, fontWeight: "bold", color: C.red },
  staffName: { fontSize: 8, fontWeight: "bold", color: C.text },
  staffSub: { fontSize: 7, color: C.textMuted },

  /* Legend */
  legendGrid: { flexDirection: "row", flexWrap: "wrap", gap: 10, marginBottom: 12 },
  legendItem: { flexDirection: "row", gap: 16, flex: 1, minWidth: "45%" },
  legendLabel: { fontSize: 7, fontWeight: "bold", color: C.greyDark, width: 70 },
  legendDesc: { fontSize: 7, color: C.textMuted, flex: 1 },

  /* Footer */
  footer: { position: "absolute", bottom: 16, left: 36, right: 36, flexDirection: "row", justifyContent: "space-between", alignItems: "center", borderTopWidth: 0.5, borderTopColor: C.border, paddingTop: 8 },
  footerLeft: { flexDirection: "row", alignItems: "center", gap: 6 },
  footerText: { fontSize: 6, color: C.textMuted },
  footerRight: { textAlign: "right" },

  /* ─── Matrix page ─── */
  matrixHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: C.greyBorder },
  matrixTitle: { fontSize: 14, fontWeight: "bold", color: C.text },
  matrixSub: { fontSize: 7, color: C.textMuted, marginTop: 2 },
  matrixClientInfo: { textAlign: "right" },
  matrixClientName: { fontSize: 10, fontWeight: "bold", color: C.text },
  matrixClientDob: { fontSize: 7, color: C.textMuted },

  shiftLegend: { flexDirection: "row", justifyContent: "center", gap: 16, marginBottom: 10 },
  shiftBadge: { paddingVertical: 2, paddingHorizontal: 10, borderRadius: 10, borderWidth: 1 },

  /* Table */
  tableContainer: { marginBottom: 14 },
  medTableHeader: { flexDirection: "row", alignItems: "center", marginBottom: 4 },
  medTableName: { fontSize: 8, fontWeight: "bold", color: C.text },
  medTableDetail: { fontSize: 6, color: C.textMuted, marginLeft: 6 },
  scheduleBadge: { backgroundColor: C.amberLight, borderRadius: 3, paddingVertical: 1, paddingHorizontal: 6, marginLeft: 8 },
  scheduleBadgeText: { fontSize: 5.5, fontWeight: "bold", color: C.amber },

  dayHeaderRow: { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: C.greyBorder },
  dayHeaderCell: { width: 28, textAlign: "center", paddingVertical: 2 },
  dayHeaderDay: { fontSize: 5, color: C.textMuted },
  dayHeaderNum: { fontSize: 7, fontWeight: "bold", color: C.text },

  shiftRow: { flexDirection: "row", borderBottomWidth: 0.3, borderBottomColor: C.greyBorder, minHeight: 18 },
  shiftLabel: { width: 28, justifyContent: "center", alignItems: "center", backgroundColor: C.greyLight, borderRightWidth: 0.3, borderRightColor: C.greyBorder },
  shiftLabelText: { fontSize: 5.5, fontWeight: "bold", color: C.greyDark },
  countCell: { width: 28, justifyContent: "center", alignItems: "center", paddingVertical: 1.5, borderRightWidth: 0.3, borderRightColor: C.greyBorder },
  countValue: { fontSize: 7, fontWeight: "bold", color: C.text },
  countStaff: { fontSize: 4.5, color: C.textMuted },
  countCellDiscrepancy: { backgroundColor: "#FFF8E1" },
  endCountCell: { width: 30, justifyContent: "center", alignItems: "center", backgroundColor: C.greyLight },
  openingCountCell: { width: 38, justifyContent: "center", alignItems: "center", backgroundColor: C.greyLight },
  endCountValue: { fontSize: 7, fontWeight: "bold", color: C.text },
  openingCountValue: { fontSize: 10, fontWeight: "bold", color: C.amber },

  /* Signature row */
  sigRow: { flexDirection: "row", justifyContent: "space-between", marginTop: 14, paddingTop: 10, borderTopWidth: 1, borderTopColor: C.greyBorder },
  sigBlock: { width: "30%" },
  sigTitle: { fontSize: 7, fontWeight: "bold", color: C.greyDark, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 },
  sigLine: { borderBottomWidth: 1, borderBottomColor: C.text, marginBottom: 4 },
  sigCaption: { fontSize: 6, color: C.textMuted },

  /* ─── Discrepancy page ─── */
  discHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: C.greyBorder },
  discTitle: { fontSize: 14, fontWeight: "bold", color: C.text },
  discAlert: { backgroundColor: C.amberLight, borderWidth: 1, borderColor: C.amberBorder, borderRadius: 4, padding: "10px 14px", marginBottom: 16, flexDirection: "row", gap: 8 },
  discAlertIcon: { fontSize: 10, color: C.amber, fontWeight: "bold" },
  discAlertText: { fontSize: 7, color: C.greyDark, flex: 1 },
  discAlertBold: { fontSize: 7, fontWeight: "bold", color: C.greyDark },

  discEntry: { borderWidth: 1, borderColor: C.greyBorder, borderRadius: 4, marginBottom: 12, backgroundColor: C.white, overflow: "hidden" },
  discEntryHeader: { backgroundColor: C.amberLight, padding: "8px 12px", flexDirection: "row", justifyContent: "space-between", alignItems: "center", borderBottomWidth: 1, borderBottomColor: C.amberBorder },
  discEntryTitle: { fontSize: 8, fontWeight: "bold", color: C.greyDark },
  discResolvedBadge: { backgroundColor: C.greenLight, borderRadius: 3, paddingVertical: 2, paddingHorizontal: 8 },
  discResolvedText: { fontSize: 6, fontWeight: "bold", color: C.green },
  discInfoGrid: { flexDirection: "row", padding: "8px 12px", borderBottomWidth: 0.5, borderBottomColor: C.greyBorder },
  discInfoCell: { flex: 1, paddingHorizontal: 4 },
  discInfoLabel: { fontSize: 6, fontWeight: "bold", color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 },
  discInfoValue: { fontSize: 8, color: C.text },
  discInfoValueLarge: { fontSize: 12, fontWeight: "bold", color: C.text },
  discInfoValueRed: { fontSize: 12, fontWeight: "bold", color: C.red },
  discDesc: { padding: "8px 12px", borderBottomWidth: 0.5, borderBottomColor: C.greyBorder },
  discDescTitle: { fontSize: 7, fontWeight: "bold", color: C.greyDark, textTransform: "uppercase", marginBottom: 4 },
  discDescText: { fontSize: 7, color: C.text, lineHeight: 1.5 },
  discStaffRow: { flexDirection: "row", padding: "8px 12px" },
  discStaffCell: { flex: 1, paddingHorizontal: 4 },

  noDiscBox: { backgroundColor: C.greyLight, borderWidth: 1, borderColor: C.greyBorder, borderRadius: 4, padding: 20, justifyContent: "center", alignItems: "center", marginBottom: 16 },
  noDiscText: { fontSize: 8, color: C.textMuted, fontStyle: "italic" },

  /* Disc page signature */
  discSigRow: { flexDirection: "row", justifyContent: "space-between", marginTop: 20, paddingTop: 10, borderTopWidth: 1, borderTopColor: C.greyBorder },
});

/* ─── Logo component ─── */
const Logo = ({ photo, name, size = 36 }) => {
  if (photo && photo !== "undefined" && photo !== "null") {
    try {
      return <Image src={photo} style={{ width: size, height: size, borderRadius: size / 2, objectFit: "cover" }} />;
    } catch (e) { /* fallback */ }
  }
  return (
    <View style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: C.amber, justifyContent: "center", alignItems: "center" }}>
      <Text style={{ fontSize: size * 0.36, fontWeight: "bold", color: C.white }}>{getInitials(name)}</Text>
    </View>
  );
};

/* ─── Footer component ─── */
const Footer = ({ facilityName, pageNum, totalPages, extra }) => (
  <View style={s.footer} fixed>
    <View style={s.footerLeft}>
      <View style={{ width: 14, height: 14, borderRadius: 7, backgroundColor: C.amber, justifyContent: "center", alignItems: "center" }}>
        <Text style={{ fontSize: 6, fontWeight: "bold", color: C.white }}>{getInitials(facilityName)}</Text>
      </View>
      <View>
        <Text style={{ fontSize: 6, fontWeight: "bold", color: C.text }}>Generated using Ogiso EHR</Text>
        <Text style={s.footerText}>{facilityName}  |  Controlled - Authorized Personnel Only</Text>
      </View>
    </View>
    <View style={s.footerRight}>
      <Text style={{ fontSize: 7, fontWeight: "bold", color: C.text }}>Page {pageNum}</Text>
      {extra ? <Text style={s.footerText}>{extra}</Text> : null}
    </View>
  </View>
);

/* ─── COVER PAGE ─── */
const CoverPage = ({ clientData, narcoticMeds, facilityName, facilityPhoto, staffList, reportMonth, reportYear, daysInMonth, medMatrix, discrepancies }) => {
  const clientName = clientData?.name || "Client";
  const dobRaw = clientData?.form1?.dob || clientData?.dob;
  const dob = dobRaw ? moment(dobRaw).format("MM/DD/YYYY") : "";
  const age = dobRaw ? moment().diff(moment(dobRaw), "years") : "";
  const sex = clientData?.form1?.gender || clientData?.sex || clientData?.gender || "";
  const rawAllergies = clientData?.form1?.knownAllergies || clientData?.allergies || clientData?.allergy;
  const allergies = Array.isArray(rawAllergies) ? rawAllergies.filter(Boolean).join(", ") : (rawAllergies || "");
  const levelOfCare = clientData?.form1?.levelofcare || clientData?.levelOfCare || clientData?.careLevel || "";
  const monthLabel = moment(`${reportYear}-${String(reportMonth).padStart(2, "0")}`, "YYYY-MM").format("MMMM YYYY");
  const periodStart = moment(`${reportYear}-${String(reportMonth).padStart(2, "0")}-01`).format("MM/DD/YYYY");
  const periodEnd = moment(`${reportYear}-${String(reportMonth).padStart(2, "0")}-${daysInMonth}`).format("MM/DD/YYYY");
  const totalShiftEntries = daysInMonth * 3;
  const discCount = discrepancies?.length || 0;
  const now = moment().format("MM/DD/YYYY [at] hh:mm A");

  return (
    <Page size="LETTER" style={s.page}>
      {/* Dark header */}
      <View style={s.coverHeader}>
        <View style={s.coverTopRow}>
          <View style={s.coverLogoRow}>
            <Logo photo={facilityPhoto} name={facilityName} />
            <View>
              <Text style={{ fontSize: 10, fontWeight: "bold", color: C.white }}>{facilityName}</Text>
              <Text style={{ fontSize: 7, color: "#9CA3AF" }}>Electronic Health Record</Text>
            </View>
          </View>
          <View>
            <Text style={s.coverGenText}>Generated using Ogiso EHR</Text>
            <Text style={s.coverGenText}>{now}</Text>
          </View>
        </View>

        <View style={s.coverBadge}>
          <Text style={s.coverBadgeText}>Controlled Substance &amp; Narcotic Monthly Count</Text>
        </View>

        <Text style={s.coverClientName}>{clientName}</Text>
        <Text style={s.coverSubtitle}>{facilityName}  |  {monthLabel}</Text>
      </View>

      {/* Body */}
      <View style={s.coverBody}>
        {/* Client Info + Report Details */}
        <View style={s.infoGrid}>
          <View style={s.infoCol}>
            <Text style={s.sectionTitle}>Client Information</Text>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Full name</Text>
              <Text style={s.infoValue}>{clientName}</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Date of birth</Text>
              <Text style={s.infoValue}>{dob}{age ? ` (${age} years)` : ""}</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Sex</Text>
              <Text style={s.infoValue}>{sex}</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Facility</Text>
              <Text style={s.infoValue}>{facilityName}</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Allergies</Text>
              <Text style={[s.infoValue, allergies ? { color: C.red, fontWeight: "bold" } : {}]}>{allergies || "None known"}</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Level of care</Text>
              <Text style={s.infoValue}>{levelOfCare || "-"}</Text>
            </View>
          </View>

          <View style={s.infoCol}>
            <Text style={s.sectionTitle}>Report Details</Text>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Report type</Text>
              <Text style={s.infoValue}>Monthly Narcotic Count</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Report period</Text>
              <Text style={s.infoValue}>{periodStart} - {periodEnd}</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Controlled substances</Text>
              <Text style={s.infoValueHighlight}>{narcoticMeds.length} medication{narcoticMeds.length !== 1 ? "s" : ""}</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Total shift entries</Text>
              <Text style={s.infoValue}>{totalShiftEntries} ({daysInMonth} days x 3 shifts)</Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Discrepancies noted</Text>
              <Text style={discCount > 0 ? s.infoValueRed : s.infoValue}>
                {discCount > 0 ? `${discCount} (see discrepancy log)` : "0"}
              </Text>
            </View>
            <View style={s.infoRow}>
              <Text style={s.infoLabel}>Generated</Text>
              <Text style={s.infoValue}>{now}</Text>
            </View>
          </View>
        </View>

        {/* Controlled Substances */}
        <Text style={[s.sectionTitle, { marginTop: 20 }]}>Controlled Substances on This Record</Text>
        <View style={s.substanceRow}>
          {narcoticMeds.map((med, i) => {
            const medId = String(med.id || med.medicationId || med._id);
            const matrix = medMatrix[medId];
            const schedule = med.schedule || med.deaSchedule || "";
            const dosageInfo = [med.dosage || med.amount, med.unit, med.dosageForm].filter(Boolean).join(" ");
            const isDC = med.discontinued;
            const dcDate = med.discontinuedDate || med.discontinuedAt || med.discontinuedOn || med.updatedAt;
            return (
              <View key={i} style={[s.substanceCard, isDC ? { borderColor: "#BDBDBD", opacity: 0.85 } : {}]}>
                <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                  <Text style={s.substanceName}>{med.medicationName || med.name}</Text>
                  {isDC ? (
                    <View style={{ backgroundColor: "#FFEBEE", borderRadius: 3, paddingVertical: 1, paddingHorizontal: 6 }}>
                      <Text style={{ fontSize: 5.5, fontWeight: "bold", color: C.red }}>D/C {dcDate ? moment(dcDate).format("MM/DD") : ""}</Text>
                    </View>
                  ) : null}
                </View>
                <Text style={s.substanceDetail}>{dosageInfo}{schedule ? `  |  ${schedule}` : ""}</Text>
                <Text style={s.substanceCount}>
                  Opening count: {matrix?.openingCount ?? "-"}  |  Closing count: {matrix?.closingCount ?? "-"}
                </Text>
              </View>
            );
          })}
        </View>

        {/* Staff */}
        {staffList?.length > 0 && (
          <View>
            <Text style={s.sectionTitle}>Staff on This Record</Text>
            <View style={s.staffRow}>
              {staffList.map((st, i) => (
                <View key={i} style={s.staffCard}>
                  <View style={s.staffCircle}>
                    <Text style={s.staffInitial}>{st.initials}</Text>
                  </View>
                  <View>
                    <Text style={s.staffName}>{st.name}</Text>
                    <Text style={s.staffSub}>Initial: {st.initials}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Legend */}
        <Text style={s.sectionTitle}>How to Read the Count Matrix</Text>
        <View style={s.legendGrid}>
          <View style={s.legendItem}>
            <Text style={s.legendLabel}>Cell value</Text>
            <Text style={s.legendDesc}>Running count of tablets remaining after that shift</Text>
          </View>
          <View style={s.legendItem}>
            <Text style={s.legendLabel}>Staff initial</Text>
            <Text style={s.legendDesc}>Small text below the count - initials of staff who performed the count</Text>
          </View>
          <View style={s.legendItem}>
            <Text style={s.legendLabel}>Highlighted cell</Text>
            <Text style={s.legendDesc}>Amber background indicates a count discrepancy - see discrepancy log</Text>
          </View>
          <View style={s.legendItem}>
            <Text style={s.legendLabel}>Empty cell</Text>
            <Text style={s.legendDesc}>No count recorded for that shift on that day</Text>
          </View>
        </View>
      </View>

      <Footer facilityName={facilityName} pageNum="1" extra="Count matrix on next page" />
    </Page>
  );
};

/* ─── MATRIX PAGE ─── */
const MatrixPage = ({ clientData, narcoticMeds, facilityName, facilityPhoto, medMatrix, daysInMonth, reportMonth, reportYear, totalPages }) => {
  const clientName = clientData?.name || "Client";
  const dobRaw2 = clientData?.form1?.dob || clientData?.dob;
  const dob = dobRaw2 ? moment(dobRaw2).format("MM/DD/YYYY") : "";
  const monthLabel = moment(`${reportYear}-${String(reportMonth).padStart(2, "0")}`, "YYYY-MM").format("MMMM YYYY");
  const rawAllergies2 = clientData?.form1?.knownAllergies || clientData?.allergies || clientData?.allergy;
  const allergies = Array.isArray(rawAllergies2) ? rawAllergies2.filter(Boolean).join(", ") : (rawAllergies2 || "");
  const shifts = ["AM", "PM", "NOC"];

  // Day of week labels
  const dayLabels = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const dayMoment = moment(`${reportYear}-${String(reportMonth).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    dayLabels.push({ num: d, day: dayMoment.format("dd").charAt(0) + dayMoment.format("dd").charAt(1) });
  }

  return (
    <Page size="LETTER" orientation="landscape" style={s.pageLandscape}>
      {/* Header */}
      <View style={s.matrixHeader}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
          <Logo photo={facilityPhoto} name={facilityName} size={28} />
          <View>
            <Text style={{ fontSize: 8, fontWeight: "bold", color: C.text }}>{facilityName}</Text>
            <Text style={{ fontSize: 6, color: C.textMuted }}>Monthly Narcotic Count - {clientName}</Text>
          </View>
        </View>

        <View>
          <Text style={s.matrixTitle}>Monthly Narcotic Count</Text>
          <Text style={s.matrixSub}>Running count per shift  |  Staff initial below count</Text>
          <View style={[s.shiftLegend, { marginTop: 4, justifyContent: "flex-start" }]}>
            {[
              { label: "AM", bg: "#FFF8E1", border: "#FFE082" },
              { label: "PM", bg: "#E8F5E9", border: "#A5D6A7" },
              { label: "NOC (Overnight)", bg: "#EDE9FE", border: "#C4B5FD" },
            ].map((sh, i) => (
              <View key={i} style={[s.shiftBadge, { backgroundColor: sh.bg, borderColor: sh.border }]}>
                <Text style={{ fontSize: 5.5, fontWeight: "bold", color: C.greyDark }}>{sh.label}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={s.matrixClientInfo}>
          <Text style={s.matrixClientName}>{clientName}</Text>
          <Text style={s.matrixClientDob}>DOB: {dob}  |  {monthLabel}</Text>
          {allergies ? <Text style={{ fontSize: 6, fontWeight: "bold", color: C.red }}>Allergies: {allergies}</Text> : null}
        </View>
      </View>

      {/* Day header row */}
      <View style={[s.dayHeaderRow, { backgroundColor: C.greyLight }]}>
        <View style={{ width: 28 }} />
        {dayLabels.map((dl, i) => (
          <View key={i} style={s.dayHeaderCell}>
            <Text style={s.dayHeaderDay}>{dl.day}</Text>
            <Text style={s.dayHeaderNum}>{dl.num}</Text>
          </View>
        ))}
        <View style={[s.dayHeaderCell, { width: 30 }]}>
          <Text style={s.dayHeaderDay}>End</Text>
          <Text style={s.dayHeaderNum}>Count</Text>
        </View>
        <View style={[s.dayHeaderCell, { width: 38 }]}>
          <Text style={s.dayHeaderDay}>Opening</Text>
          <Text style={s.dayHeaderNum}>Count</Text>
        </View>
      </View>

      {/* Medication rows */}
      {narcoticMeds.map((med, mi) => {
        const medId = String(med.id || med.medicationId || med._id);
        const matrix = medMatrix[medId];
        if (!matrix) return null;
        const schedule = med.schedule || med.deaSchedule || "";
        const dosageInfo = [med.dosage || med.amount, med.unit, med.dosageForm].filter(Boolean).join(" ");

        return (
          <View key={mi} style={s.tableContainer} wrap={false}>
            {/* Med name header */}
            <View style={s.medTableHeader}>
              <Text style={s.medTableName}>{med.medicationName || med.name}</Text>
              <Text style={s.medTableDetail}>{dosageInfo}</Text>
              {schedule ? (
                <View style={s.scheduleBadge}>
                  <Text style={s.scheduleBadgeText}>{schedule}</Text>
                </View>
              ) : null}
              {matrix.dcDay !== null && matrix.dcDay !== undefined ? (
                <View style={{ backgroundColor: "#FFEBEE", borderRadius: 3, paddingVertical: 1, paddingHorizontal: 6, marginLeft: 8 }}>
                  <Text style={{ fontSize: 5.5, fontWeight: "bold", color: C.red }}>
                    D/C {med.discontinuedDate || med.discontinuedAt || med.discontinuedOn ? moment(med.discontinuedDate || med.discontinuedAt || med.discontinuedOn).format("MM/DD") : ""}
                  </Text>
                </View>
              ) : null}
            </View>

            {/* Shift rows */}
            {shifts.map((shift, si) => {
              // Find last count for this shift across all days for end count
              let endCount = null;
              for (let d = daysInMonth; d >= 1; d--) {
                const cell = matrix.days[d]?.[shift];
                if (cell && cell.count !== null && cell.count !== undefined) {
                  endCount = cell.count;
                  break;
                }
              }

              return (
                <View key={si} style={s.shiftRow}>
                  <View style={[s.shiftLabel, {
                    backgroundColor: shift === "AM" ? "#FFF8E1" : shift === "PM" ? "#E8F5E9" : "#EDE9FE",
                  }]}>
                    <Text style={s.shiftLabelText}>{shift}</Text>
                  </View>

                  {/* Day cells */}
                  {Array.from({ length: daysInMonth }, (_, d) => d + 1).map((day) => {
                    const cell = matrix.days[day]?.[shift];
                    const hasValue = cell && cell.count !== null && cell.count !== undefined;
                    const isDC = matrix.dcDay !== null && matrix.dcDay !== undefined && day > matrix.dcDay;
                    return (
                      <View key={day} style={[s.countCell, cell?.discrepancy ? s.countCellDiscrepancy : {}, isDC ? { backgroundColor: "#F5F5F5" } : {}]}>
                        {isDC ? (
                          <Text style={{ fontSize: 5.5, fontWeight: "bold", color: "#9E9E9E" }}>D/C</Text>
                        ) : hasValue ? (
                          <View>
                            <Text style={s.countValue}>{cell.count}</Text>
                            {cell.staffInitial ? <Text style={s.countStaff}>{cell.staffInitial}</Text> : null}
                          </View>
                        ) : null}
                      </View>
                    );
                  })}

                  {/* End count */}
                  <View style={s.endCountCell}>
                    <Text style={s.endCountValue}>{endCount !== null ? endCount : "-"}</Text>
                  </View>

                  {/* Opening count (only on first shift row) */}
                  {si === 0 ? (
                    <View style={[s.openingCountCell, { borderLeftWidth: 1, borderLeftColor: C.greyBorder }]}>
                      <Text style={s.openingCountValue}>{matrix.openingCount}</Text>
                    </View>
                  ) : (
                    <View style={[s.openingCountCell, { borderLeftWidth: 1, borderLeftColor: C.greyBorder }]} />
                  )}
                </View>
              );
            })}
          </View>
        );
      })}

      {/* Signature row */}
      <View style={s.sigRow}>
        <View style={s.sigBlock}>
          <Text style={s.sigTitle}>Supervisor Review</Text>
          <View style={s.sigLine} />
          <Text style={s.sigCaption}>Name &amp; signature  |  Date: ___________</Text>
        </View>
        <View style={s.sigBlock}>
          <Text style={s.sigTitle}>DON / Administrator</Text>
          <View style={s.sigLine} />
          <Text style={s.sigCaption}>Name &amp; signature  |  Date: ___________</Text>
        </View>
        <View style={s.sigBlock}>
          <Text style={s.sigTitle}>See Discrepancy Log</Text>
          <Text style={{ fontSize: 6, color: C.textMuted }}>Refer to Page 3 for full discrepancy details</Text>
        </View>
      </View>

      {/* Footer */}
      <View style={{ position: "absolute", bottom: 10, left: 16, right: 16, flexDirection: "row", justifyContent: "space-between", borderTopWidth: 0.5, borderTopColor: C.greyBorder, paddingTop: 6 }} fixed>
        <Text style={{ fontSize: 5, color: C.textMuted }}>{facilityName} | Controlled Substance Monthly Count - Confidential</Text>
        <Text style={{ fontSize: 5, color: C.textMuted }}>Generated by Ogiso EHR  |  Page 2 of {totalPages}</Text>
      </View>
    </Page>
  );
};

/* ─── DISCREPANCY PAGE ─── */
const DiscrepancyPage = ({ clientData, facilityName, facilityPhoto, discrepancies, reportMonth, reportYear, totalPages }) => {
  const clientName = clientData?.name || "Client";
  const monthLabel = moment(`${reportYear}-${String(reportMonth).padStart(2, "0")}`, "YYYY-MM").format("MMMM YYYY");
  const discCount = discrepancies?.length || 0;

  return (
    <Page size="LETTER" style={[s.page, { padding: 36 }]}>
      {/* Header */}
      <View style={s.discHeader}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
          <Logo photo={facilityPhoto} name={facilityName} size={28} />
          <View>
            <Text style={{ fontSize: 8, fontWeight: "bold", color: C.text }}>{facilityName}</Text>
            <Text style={{ fontSize: 6, color: C.textMuted }}>Narcotic Count Discrepancy Log - {monthLabel}</Text>
          </View>
        </View>
        <View style={{ textAlign: "right" }}>
          <Text style={s.discTitle}>Discrepancy Log</Text>
          <Text style={{ fontSize: 7, color: C.textMuted }}>{clientName}  |  Page {totalPages}</Text>
        </View>
      </View>

      {/* Alert banner */}
      <View style={s.discAlert}>
        <Text style={s.discAlertIcon}>!</Text>
        <View style={{ flex: 1 }}>
          <Text style={s.discAlertText}>
            <Text style={s.discAlertBold}>{discCount} discrepanc{discCount === 1 ? "y" : "ies"} recorded this month.</Text>
            <Text> All discrepancies must be reviewed by the supervisor and DON. Unresolved discrepancies must be reported to the State Board of Pharmacy within 72 hours.</Text>
          </Text>
        </View>
      </View>

      {/* Discrepancy entries */}
      <Text style={[s.sectionTitle, { marginTop: 4 }]}>Discrepancy Entries</Text>

      {discCount === 0 ? (
        <View style={s.noDiscBox}>
          <Text style={s.noDiscText}>No discrepancies recorded for {monthLabel}</Text>
        </View>
      ) : (
        discrepancies.map((disc, i) => (
          <View key={i} style={s.discEntry} wrap={false}>
            <View style={s.discEntryHeader}>
              <Text style={s.discEntryTitle}>
                Discrepancy #{i + 1}  |  {disc.medicationName}  |  {disc.date} - {disc.shift} Shift
              </Text>
              <View style={disc.resolved ? s.discResolvedBadge : {}}>
                <Text style={disc.resolved ? s.discResolvedText : { fontSize: 6, fontWeight: "bold", color: C.red }}>
                  {disc.resolved ? "Resolved" : "Unresolved"}
                </Text>
              </View>
            </View>
            <View style={s.discInfoGrid}>
              <View style={s.discInfoCell}>
                <Text style={s.discInfoLabel}>Medication</Text>
                <Text style={s.discInfoValue}>{disc.medicationName}</Text>
                <Text style={{ fontSize: 6, color: C.textMuted }}>{disc.dosageInfo}</Text>
              </View>
              <View style={s.discInfoCell}>
                <Text style={s.discInfoLabel}>Date &amp; Shift</Text>
                <Text style={s.discInfoValue}>{disc.date}</Text>
                <Text style={{ fontSize: 6, color: C.textMuted }}>{disc.shift} Shift</Text>
              </View>
              <View style={s.discInfoCell}>
                <Text style={s.discInfoLabel}>Expected Count</Text>
                <Text style={s.discInfoValueLarge}>{disc.expected}</Text>
              </View>
              <View style={s.discInfoCell}>
                <Text style={s.discInfoLabel}>Actual Count</Text>
                <Text style={s.discInfoValueLarge}>{disc.actual}</Text>
              </View>
              <View style={s.discInfoCell}>
                <Text style={s.discInfoLabel}>Variance</Text>
                <Text style={s.discInfoValueRed}>{disc.variance > 0 ? `+${disc.variance}` : disc.variance} tablet{Math.abs(disc.variance) !== 1 ? "s" : ""}</Text>
              </View>
            </View>
            {disc.description ? (
              <View style={s.discDesc}>
                <Text style={s.discDescTitle}>Description &amp; Resolution</Text>
                <Text style={s.discDescText}>{disc.description}</Text>
              </View>
            ) : null}
          </View>
        ))
      )}

      {/* Additional discrepancy entries placeholder */}
      {discCount > 0 && (
        <View>
          <Text style={[s.sectionTitle, { marginTop: 8 }]}>Additional Discrepancy Entries</Text>
          <View style={s.noDiscBox}>
            <Text style={s.noDiscText}>No additional discrepancies recorded for {monthLabel}</Text>
          </View>
        </View>
      )}

      {/* Monthly review signatures */}
      <View style={[s.discSigRow, s.sigRow]}>
        <View style={s.sigBlock}>
          <Text style={s.sigTitle}>Monthly Review - Supervisor</Text>
          <View style={s.sigLine} />
          <Text style={s.sigCaption}>Name &amp; signature  |  Date: ___________</Text>
        </View>
        <View style={s.sigBlock}>
          <Text style={s.sigTitle}>Monthly Review - DON / Administrator</Text>
          <View style={s.sigLine} />
          <Text style={s.sigCaption}>Name &amp; signature  |  Date: ___________</Text>
        </View>
        <View style={s.sigBlock}>
          <Text style={s.sigTitle}>Pharmacy Notification (If Required)</Text>
          <View style={s.sigLine} />
          <Text style={s.sigCaption}>Notified: ___________  |  Method: ___________</Text>
        </View>
      </View>

      {/* Footer */}
      <View style={s.footer} fixed>
        <View style={s.footerLeft}>
          <View style={{ width: 14, height: 14, borderRadius: 7, backgroundColor: C.amber, justifyContent: "center", alignItems: "center" }}>
            <Text style={{ fontSize: 6, fontWeight: "bold", color: C.white }}>{getInitials(facilityName)}</Text>
          </View>
          <View>
            <Text style={{ fontSize: 6, fontWeight: "bold", color: C.text }}>Generated using Ogiso EHR</Text>
            <Text style={s.footerText}>{facilityName}  |  Controlled Substance Monthly Count - Confidential</Text>
          </View>
        </View>
        <View style={s.footerRight}>
          <Text style={{ fontSize: 7, fontWeight: "bold", color: C.text }}>Page {totalPages} of {totalPages}</Text>
        </View>
      </View>
    </Page>
  );
};

/* ─── MAIN DOCUMENT ─── */
const NarcoticsReportPDF = ({
  clientData,
  narcoticMeds = [],
  marRecords = [],
  facilityName = "Facility",
  facilityPhoto,
  reportMonth,
  reportYear,
}) => {
  const { medMatrix, daysInMonth, staffList, discrepancies } = processNarcoticData(
    marRecords,
    narcoticMeds,
    reportMonth,
    reportYear
  );

  const totalPages = 3;

  return (
    <Document>
      <CoverPage
        clientData={clientData}
        narcoticMeds={narcoticMeds}
        facilityName={facilityName}
        facilityPhoto={facilityPhoto}
        staffList={staffList}
        reportMonth={reportMonth}
        reportYear={reportYear}
        daysInMonth={daysInMonth}
        medMatrix={medMatrix}
        discrepancies={discrepancies}
      />
      <MatrixPage
        clientData={clientData}
        narcoticMeds={narcoticMeds}
        facilityName={facilityName}
        facilityPhoto={facilityPhoto}
        medMatrix={medMatrix}
        daysInMonth={daysInMonth}
        reportMonth={reportMonth}
        reportYear={reportYear}
        totalPages={totalPages}
      />
      <DiscrepancyPage
        clientData={clientData}
        facilityName={facilityName}
        facilityPhoto={facilityPhoto}
        discrepancies={discrepancies}
        reportMonth={reportMonth}
        reportYear={reportYear}
        totalPages={totalPages}
      />
    </Document>
  );
};

export default NarcoticsReportPDF;
